import { useMemo, useState } from 'react';
import { fa, toLatin, toman, qty } from './fa';
import { tillBuild } from './build';
import type { CatalogueItem } from './types';

/*
 * The things that come up mid-sale — screen ۰۵ of the design.
 *
 * The one that earns its place is the price check. A customer asks "how much is
 * this?" while another is halfway through paying, and without somewhere to look
 * it up the cashier scans it into the open basket and then has to remember to
 * take it out again. The design says it out loud and so does this: a price
 * check never touches the current sale.
 */

export function ToolsScreen({
  catalogue, lastSale, onBack, canOpenDrawer, resolvePanel, agent,
}: {
  catalogue: CatalogueItem[];
  lastSale: { number: string; totalRial: number } | null;
  onBack: () => void;
  canOpenDrawer: boolean;
  /*
   * What the local agent says about itself, from /v1/status.
   *
   * The header carries the same two stamps in nine-point type, which answers
   * "did the install land" at a glance. This card is the version of that for a
   * phone call: the device id to say out loud, and version separated from
   * build, because on a wrapper-hosted agent those two are different numbers
   * from different places and a support conversation that conflates them goes
   * in circles.
   */
  agent?: { deviceId?: string; version?: string; build?: string };
  /*
   * Held card payments, resolvable from here as well as from the payment
   * screen. A payment can be left ambiguous on a basket nobody has open any
   * more — the cashier walked away, the till restarted — and it still blocks
   * that basket. This is where a سرشیفت finds one without having to recreate
   * the sale that produced it.
   */
  resolvePanel?: React.ReactNode;
}) {
  const [query, setQuery] = useState('');

  const hit = useMemo(() => {
    const q = toLatin(query.trim()).toLowerCase();
    if (!q) return null;
    return (
      catalogue.find((i) => toLatin(i.barcode ?? '') === q || toLatin(i.sku ?? '') === q) ??
      catalogue.find((i) => toLatin(i.name).toLowerCase().includes(q)) ??
      null
    );
  }, [query, catalogue]);

  return (
    <div style={{ flex: 1, padding: 18, display: 'flex', flexDirection: 'column', gap: 14, minHeight: 0 }}>
      {resolvePanel ? (
        <div style={{ marginBottom: 4 }}>{resolvePanel}</div>
      ) : null}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          onClick={onBack}
          style={{
            width: 44, height: 44, borderRadius: 10, border: '1px solid var(--luma-line-strong)',
            background: 'var(--luma-card)', fontSize: 16,
          }}
        >
          →
        </button>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.1em', color: 'var(--luma-placeholder)' }}>
            ۰۵ · ابزار سریع
          </div>
          <div style={{ fontWeight: 800, fontSize: 24 }}>کارهایی که وسط فروش لازم می‌شود</div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, alignContent: 'start' }}>
        <div
          style={{
            gridColumn: 'span 2', background: 'var(--luma-card)', border: '1px solid var(--luma-line)',
            borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 12,
          }}
        >
          <div style={{ fontWeight: 800, fontSize: 17 }}>استعلام قیمت</div>
          <div
            style={{
              height: 56, background: 'var(--luma-sunken)', border: '1px solid var(--luma-line-strong)',
              borderRadius: 12, display: 'flex', alignItems: 'center', gap: 10, padding: '0 14px',
            }}
          >
            <span style={{ fontSize: 18, color: 'var(--luma-placeholder)' }}>⌕</span>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="بارکد بزنید یا نام کالا را بنویسید…"
              autoFocus
              style={{ flex: 1, height: '100%', border: 0, background: 'transparent', fontSize: 16, fontWeight: 500 }}
            />
          </div>

          {hit ? (
            <div
              style={{
                background: 'var(--luma-accent-tint)', border: '1px solid var(--luma-tint-warning-border)',
                borderRadius: 12, padding: 14, display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', gap: 12,
              }}
            >
              <div>
                <div style={{ fontWeight: 700, fontSize: 16 }}>{hit.name}</div>
                <div style={{ fontSize: 12.5, color: 'var(--luma-accent-tint-text)' }}>
                  {hit.tracksStock ? `موجودی ${qty(hit.onHand)}` : 'خدماتی'}
                  {hit.barcode ? ` · کد ${fa(hit.barcode)}` : ''}
                </div>
              </div>
              <div className="pos-num" style={{ fontWeight: 800, fontSize: 26, color: 'var(--luma-accent-tint-text)' }}>
                {toman(hit.priceRial)}
              </div>
            </div>
          ) : (
            <div
              style={{
                background: 'var(--luma-sunken)', border: '1px solid var(--pos-rule-strong)',
                borderRadius: 12, padding: 14, fontSize: 13.5, color: 'var(--luma-muted)',
              }}
            >
              {query.trim() ? 'کالایی با این مشخصات پیدا نشد.' : 'بارکد را بزنید تا قیمت نمایش داده شود.'}
            </div>
          )}

          <div style={{ fontSize: 12.5, color: 'var(--pos-ink-3)' }}>
            استعلام قیمت هیچ‌وقت روی فاکتور جاری اثر نمی‌گذارد — سبد مشتری دست‌نخورده می‌ماند.
          </div>
        </div>

        <Card title="آخرین فروش">
          {lastSale ? (
            <div style={{ fontSize: 12.5, color: 'var(--pos-ink-3)', lineHeight: 1.6 }}>
              فاکتور <span className="pos-num">{fa(lastSale.number)}</span> —{' '}
              <span className="pos-num">{toman(lastSale.totalRial)}</span>
            </div>
          ) : (
            <div style={{ fontSize: 12.5, color: 'var(--luma-muted)' }}>در این شیفت هنوز فروشی ثبت نشده.</div>
          )}
          {/*
            Named as not connected rather than shown as a button that does
            nothing. Reprinting needs the local agent and a thermal printer;
            a dead button makes the cashier wait for paper that never comes.
          */}
          <div style={{ fontSize: 12.5, color: 'var(--luma-muted)', marginTop: 8 }}>
            چاپ مجدد پس از اتصال چاپگر از طریق دستگاه محلی فعال می‌شود.
          </div>
        </Card>

        <Card title="نسخه‌ها">
          {/*
            pos-stamp, like the header: the body face redraws ASCII digits as
            Persian ones, which is a safety net everywhere else on this till
            and ruins the one thing a hash is for. See tokens.css.
          */}
          <div style={{ display: 'grid', gap: 6, fontSize: 12.5, color: 'var(--pos-ink-3)' }}>
            <VersionRow label="صندوق" value={tillBuild} />
            <VersionRow label="سرویس محلی" value={agent?.build} />
            {/*
              Only when it adds something. On the till these are the same
              string, and printing it twice invites the reader to look for a
              difference that is not there. On an agent hosted by a wrapper
              they differ, and then the difference is the point.
            */}
            {agent?.version && agent.version !== agent.build ? (
              <VersionRow label="میزبان" value={agent.version} />
            ) : null}
            <VersionRow label="شناسهٔ دستگاه" value={agent?.deviceId} />
          </div>
        </Card>

        <Card title="باز کردن کشو">
          <div style={{ fontSize: 12.5, color: 'var(--pos-ink-3)', lineHeight: 1.6 }}>
            {canOpenDrawer
              ? 'کشوی نقدی از طریق چاپگر حرارتی باز می‌شود و هنوز به این صندوق وصل نیست. هر بار باز شدن بدون فروش با نام شما ثبت خواهد شد.'
              : 'دسترسی باز کردن کشو ندارید.'}
          </div>
        </Card>
      </div>
    </div>
  );
}

/*
 * One label and one stamp. Absent is shown as a dash rather than as an empty
 * row: the till polls /status every thirty seconds, so a missing value means
 * the agent has not answered, and that is itself the answer to why nothing
 * else on this screen is working.
 */
function VersionRow({ label, value }: { label: string; value?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
      <span style={{ color: 'var(--luma-muted)' }}>{label}</span>
      <span
        dir="ltr"
        className="pos-stamp"
        style={{
          fontWeight: 600, fontSize: 12, textAlign: 'left', wordBreak: 'break-all',
          color: value ? 'var(--pos-ink-dark)' : 'var(--luma-placeholder)',
        }}
      >
        {value || '—'}
      </span>
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        background: 'var(--luma-card)', border: '1px solid var(--luma-line)', borderRadius: 12,
        padding: 16, display: 'flex', flexDirection: 'column', gap: 8,
      }}
    >
      <div style={{ fontWeight: 800, fontSize: 17 }}>{title}</div>
      {children}
    </div>
  );
}
